import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { RiCloseFill } from 'react-icons/ri';
import './PopupComponent.css';

interface PopupProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'mx' | 'xl';
  loading?: boolean;
  headerActions?: React.ReactNode;
}

const PopupComponent: React.FC<PopupProps> = ({
  isOpen,
  onClose,
  children,
  title,
  footer,
  size = 'md',
  loading = false,
  headerActions,
}) => {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return createPortal(
    <div className="popup-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`popup-content popup-${size}`}>
        {/* Header */}
        <div className="popup-header">
          <h2 className="popup-title">{title}</h2>
          <div className="popup-header-right">
            {headerActions}
            <button className="popup-close" onClick={onClose} aria-label="Close">
              <RiCloseFill size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="popup-body">
          {loading && (
            <div className="popup-loader">
              <div className="spinner" />
            </div>
          )}
          {children}
        </div>

        {/* Footer */}
        {footer && <div className="popup-footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default PopupComponent;